'use client';

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { PlateEditor } from "@/components/editor/plate-editor.tsx";
import { useToast } from '@/components/ui/use-toast';
import { updateNote } from '@/services/supabase/notes';
import { SettingsProvider } from '@/components/editor/settings';

/**
 * Notes Editor component using Plate editor
 */
export interface PlateNoteEditorProps {
  content?: any[];
  title?: string;
  noteId?: string | null;
  onTitleChange?: (title: string) => void;
  onContentChange?: (content: any) => void;
  isReadOnly?: boolean;
  onSave?: (content: any) => Promise<void>;
  autoSaveData?: {
    getAutoSavedContent: () => any;
    hasUnsavedContent: () => boolean;
    hasCloudSave: () => boolean;
    hasContent: () => boolean;
    isTyping: () => boolean;
  };
}

// Clean editor content to avoid circular references
const cleanContent = (content: any): any => {
  if (!content) return null;

  try {
    // Convert to string and back to remove any circular references
    return JSON.parse(JSON.stringify(content));
  } catch (e) {
    console.error('Error cleaning editor content:', e);
    return null;
  }
};

export function PlateNoteEditor({
  content = [],
  title = '',
  noteId = null,
  onTitleChange,
  onContentChange,
  isReadOnly = false,
  onSave,
  autoSaveData,
}: PlateNoteEditorProps) {
  const { toast } = useToast();

  // Initialize content with title if provided
  const initialContent = useMemo(() => {
    if (Array.isArray(content) && content.length > 0) {
      return content;
    }

    // Create default content with title if provided
    return [
      {
        type: 'h1',
        children: [{ text: title || 'Untitled Note' }],
      },
      {
        type: 'p',
        children: [{ text: '' }],
      }
    ];
  }, [content, title]);

  const [editorContent, setEditorContent] = useState<any>(initialContent);

  // Handle content change
  const handleContentChange = useCallback((newContent: any) => {
    setEditorContent(newContent);

    // Call onContentChange if provided
    if (onContentChange) {
      onContentChange(newContent);
    }

    // Extract title from first heading if available
    if (newContent && Array.isArray(newContent) && newContent.length > 0) {
      const firstNode = newContent.find(node => node.type === 'h1');
      if (firstNode && firstNode.children && firstNode.children.length > 0) {
        const newTitle = firstNode.children[0].text || '';
        if (onTitleChange) {
          onTitleChange(newTitle);
        }
      }
    }

    // No auto-save on content change - saving only happens via the save button
    // This prevents API calls on every keystroke
  }, [onContentChange, onTitleChange]);

  // Save content to database - only used by the manual save button
  const saveContent = useCallback(
    async (noteContent: any) => {
      if (!noteId && !onSave) return;

      try {
        // Clean content before saving to avoid circular references
        const cleanedContent = cleanContent(noteContent);

        // Log the content being saved for debugging
        console.log('PlateNoteEditor saving content:', cleanedContent);

        // Prioritize custom save handler over internal save mechanism
        if (onSave) {
          // Use custom save handler provided by the parent component
          await onSave(cleanedContent);

          // Don't show toast here - let the parent component handle it
          // This avoids duplicate toasts when both components show notifications
        } else if (noteId) {
          // Use default internal save behavior
          await updateNote(Number(noteId), {
            content: cleanedContent,
          });

          toast({
            title: "Saved",
            description: "Your note has been saved.",
            duration: 2000,
          });
        }
      } catch (error) {
        console.error('Error saving note:', error);
        toast({
          title: 'Save failed',
          description: 'There was an error saving your note.',
          variant: 'destructive',
        });
      }
    },
    [noteId, toast, onSave]
  );

  // No auto-save - saving only happens via the save button

  // Create a ref for the editor
  const editorRef = useRef<any>(null);

  // Store the editor instance in the ref when it's created
  const handleEditorReady = useCallback((editor: any) => {
    editorRef.current = editor;
  }, []);

  // Handle manual save
  const handleSave = useCallback(() => {
    console.log('Manual save initiated');

    // Access the editor directly to get the most up-to-date content
    if (!editorRef.current) {
      console.error('Editor not initialized');
      return;
    }

    // Get the latest content directly from the editor
    const latestContent = editorRef.current.children;

    // Log the current content for debugging
    console.log('Latest editor content at save time:', JSON.stringify(latestContent));
    console.log('Current state content:', JSON.stringify(editorContent));

    if (!isReadOnly && (noteId || onSave)) {
      saveContent(latestContent);
    }
  }, [isReadOnly, noteId, onSave, saveContent, editorContent]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Save on Ctrl+S / Cmd+S
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  // Memoize the editor component to prevent unnecessary re-renders
  const editorComponent = useMemo(() => {
    return (
      <SettingsProvider>
        <PlateEditor
          content={editorContent}
          onChange={handleContentChange}
          showSaveButton={!isReadOnly && (!!noteId || !!onSave)}
          onSave={!isReadOnly ? handleSave : undefined}
          onEditorReady={handleEditorReady}
          autoSaveData={autoSaveData}
        />
      </SettingsProvider>
    );
  }, [editorContent, handleContentChange, isReadOnly, noteId, onSave, handleSave, handleEditorReady]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="h-full w-full overflow-hidden">
        {editorComponent}
      </div>
    </DndProvider>
  );
}